import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { TableFormatter } from '@/@diffrag/table/TableFormatter'
import { api } from '@/api'
import { useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { IAdminCommunityRO } from 'api'
import { toast } from '@/lib/toast'

const BOARD_LIST = [
  {
    label: '전체',
    value: undefined,
  },
  {
    label: '공지사항',
    value: 1,
  },
  {
    label: 'Emotion',
    value: 2,
  },
  {
    label: 'Health',
    value: 3,
  },
  {
    label: '메디컬 커뮤니티',
    value: 5,
  },
]

export default function Community() {
  const q = useQueryValue('q', undefined)
  const categoryId = useQueryValue('categoryId', undefined)
  const boardId = useQueryValue('boardId', undefined)

  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    categoryId ? Number(categoryId) : undefined,
  )
  const [selectedBoard, setSelectedBoard] = useState<number | undefined>(
    boardId ? Number(boardId) : undefined,
  )

  const { data: categories } = useApi({
    id: 'categoryList',
    fn: api().adminCategory.adminCategoryList,
  })

  const page = useQueryValue('page', 1)
  const { data, isFetching, refetch } = useApi({
    id: 'communityList',
    fn: api().adminCommunity.getCommunityArticleList,
    params: {
      page,
      pageSize: 20,
      q,
      categoryId: selectedCategory,
      boardId: selectedBoard,
    },
  })

  const selectedCategoryLabels = useMemo(() => {
    if (!selectedCategory) {
      return '전체'
    }
    return (
      categories?.items?.find((item) => item.id === selectedCategory)?.name ||
      '전체'
    )
  }, [selectedCategory, categories])

  const selectedBoardLabels = useMemo(() => {
    if (!selectedBoard) {
      return '전체'
    }
    return (
      BOARD_LIST.find((item) => item.value === selectedBoard)?.label || '전체'
    )
  }, [selectedBoard])

  const onDelete = async (rows: IAdminCommunityRO[]) => {
    try {
      await Promise.all(
        rows.map((row) => api().adminCommunity.deleteCommunityArticle(row.id)),
      )

      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">카테고리 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedCategoryLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              <DropdownMenuCheckboxItem
                checked={selectedCategory === undefined}
                onCheckedChange={() => setSelectedCategory(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {categories?.items?.map((category) => (
                <DropdownMenuCheckboxItem
                  key={category.id}
                  checked={selectedCategory === category.id}
                  onCheckedChange={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">게시판 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedBoardLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              {BOARD_LIST.map((board) => (
                <DropdownMenuCheckboxItem
                  key={board.value || 'all'}
                  checked={selectedBoard === board.value}
                  onCheckedChange={() => setSelectedBoard(board.value)}
                >
                  {board.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SimpleTable
        isLoading={isFetching}
        data={data?.rows?.filter(
          (row) =>
            row.board.slug !== 'article-experience' &&
            row.board.slug !== 'notice',
        )}
        onDelete={onDelete}
        columns={[
          {
            key: 'id',
            label: 'NO',
            isPrimary: true,
            href: (data) =>
              `/community/edit?id=${data.id}&slug=${data.board.slug}`,
            width: 50,
          },
          {
            key: 'board.title',
            label: '게시판',
            isPrimary: true,
            href: (data) =>
              `/community/edit?id=${data.id}&slug=${data.board.slug}`,
            width: 120,
          },
          {
            key: 'category.name',
            label: '카테고리',
            isPrimary: true,
            width: 80,
            href: (data) =>
              `/community/edit?id=${data.id}&slug=${data.board.slug}`,
          },
          {
            key: 'title',
            label: '제목',
            isPrimary: true,
            href: (data) =>
              `/community/edit?id=${data.id}&slug=${data.board.slug}`,
          },
          {
            key: 'author.userData.nickname',
            label: '작성자',
            width: 120,
          },
          {
            key: 'createDate',
            label: '작성일',
            formatter: TableFormatter.datetime(),
            width: 150,
          },
          {
            key: 'articleStatus',
            label: '상태',
            valueMap: {
              VISIBLE: '노출',
              HIDDEN: '비노출',
            },
            width: 50,
          },
        ]}
        options={{
          disableNew: true,
          disableSort: true,
        }}
      />
      <OffsetPagination total={data?.count} page={page} pageSize={20} />
    </ContentView>
  )
}
